/**
 * What documentImportAttachToRide does with the result of one attach run.
 *
 * Extracted from the Vuex action because the action cannot be loaded by the
 * plain-node test runner (no Vue/Vuex harness here), and because these are the
 * decisions that actually protect the manager: which failures stay on screen,
 * which documents are retired, and which trip-level sections remain retryable.
 */

/**
 * Failures are now per-card, and a card's failure must outlive an unrelated
 * card's success.
 *
 * The panel attaches one card at a time as readily as it attaches all of them.
 * Replacing the whole error list after each run meant attaching card B wiped
 * card A's rejection off the screen while A was still staged and still
 * unattached — the manager was told "1 document added" and the only record of
 * the other failure was gone.
 *
 * So the list accumulates, and an entry is retired only by the run that
 * actually re-attempted the thing it describes: `attempted` is every
 * resultIndex the run sent operations for (with `null` in it when the run
 * carried the trip-level sections). Anything outside that scope is untouched.
 * The other ways an error goes away — the card is discarded, or its last point
 * removed — are dropCreationErrorsForResults, below.
 */
function mergeCreationErrors(previous, incoming, attempted) {
  const retried = new Set(attempted || []);
  const kept = (previous || []).filter(err => !retried.has(normalizeIndex(err)));
  return kept.concat(incoming || []);
}

/** An error whose card left the panel has nothing left to point at. */
function dropCreationErrorsForResults(errors, indexes) {
  const gone = new Set(indexes || []);
  return (errors || []).filter(err => !gone.has(normalizeIndex(err)));
}

/**
 * The scope one run covered, as mergeCreationErrors wants it: the distinct
 * resultIndex of every operation actually sent. `null` — the trip-level
 * sections — is a scope like any other here, so a general-information failure
 * is retired by the next run that carries those sections again, and by nothing
 * else.
 */
function attemptedResultIndexes(operations) {
  const seen = [];
  const marked = new Set();
  (operations || []).forEach((op) => {
    const index = normalizeIndex(op);
    if (marked.has(index)) return;
    marked.add(index);
    seen.push(index);
  });
  return seen;
}

/**
 * The general-information sections still owed to the trip after a run.
 *
 * Clearing them win-or-lose is how a rejected section used to be destroyed
 * outright: it belongs to no card, so once dropped there was nothing left to
 * retry and the trip simply never got it. Only the sections that landed are
 * dropped; the rejected ones stay staged for the next attach, which is also
 * what keeps the successful ones from being posted a second time.
 */
function remainingGeneralInfo(generalInfo, failedSectionIndexes) {
  const failed = new Set(failedSectionIndexes || []);
  return (generalInfo || []).filter((_, index) => failed.has(index));
}

/**
 * The results that went onto the ride in full, rewritten as retired.
 *
 * `discarded` is what takes a result out of buildAttachOperations and off the
 * panel, so a retry cannot post it twice; `attached` says WHY it left, so the
 * panel can account for the cards that vanished. Returned as {index, result}
 * pairs rather than applied in place, because the store has to hand each one to
 * Vue.set for reactivity.
 */
function attachedResultUpdates(results, indexes) {
  const updates = [];
  (indexes || []).forEach((index) => {
    const result = (results || [])[index];
    if (!result) return;
    updates.push({ index, result: { ...result, discarded: true, attached: true } });
  });
  return updates;
}

function normalizeIndex(carrier) {
  const index = carrier && carrier.resultIndex;
  return typeof index === 'number' ? index : null;
}

module.exports = {
  mergeCreationErrors,
  dropCreationErrorsForResults,
  attemptedResultIndexes,
  remainingGeneralInfo,
  attachedResultUpdates,
};
